import { Provider, computed, effect, inject, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';

import { ATTENTION_PROJECTIONS, AttentionProjection } from '@shared/services/attention/attention-projections';
import { AttentionTone } from '@shared/services/attention/attention.models';
import { CapabilityService } from '@shared/services/capability/capability.service';

import { InventoryStockService } from './services/inventory-stock.service';
import { InventorySummaryService } from './services/inventory-summary.service';
import { EVERYWHERE, needsAttentionAt } from './rules/inventory-items';
import { awaitingDelivery } from './rules/inventory-orders';

/**
 * The rail items inventory's badges decorate — the pages-config urls of its nav children. Since the
 * M1b fix round (R21, the DS rail, D220) the **Stock** row carries everything waiting on the stock —
 * low + out + transfers on the road + counts open — added up by the attention service's per-item sum;
 * **Orders** carries the orders waiting on a delivery (sent + partly received); Items carries none.
 */
export const INVENTORY_RAIL = {
  items: '/provider/app/inventory/items',
  stock: '/provider/app/inventory/stock',
  orders: '/provider/app/inventory/orders',
} as const;

/**
 * Inventory's attention projections (kz2 M1; D158) — the first module to wire them into koomzoapps'
 * shell. A projection reads a store, never a component (the DS rule), so the badges come from the
 * NGXS slice `InventoryStockService` reads, which the root store holds:
 *
 * - **Stock** — out plus low, across every location (loud, `danger`, while anything is out), counts
 *   still open or in review (`inventory.counts`) and transfers on the road (`inventory.transfers`):
 *   three rules on the one row, each gated by its own capability (M1b fix, R21 — R14 superseded).
 * - **Orders** — purchase orders sent or partly received (`inventory.purchase`): what is still
 *   coming. A draft is not waiting on anybody, and a received order is history.
 *
 * The tenant's stock is followed from the moment the person enters the inventory app (the shell is
 * app-scoped, so that is when its rail shows) and stays followed for the session, so a badge moves
 * when a posting on another device lands. Before that — from sign-in, whatever app is open — the
 * badges read the server's Inventory summary (`inventory-summary/{tenantId}`, kz2 M12 leftovers: ONE
 * document, kept by the stock-summary triggers with the same rules): the launcher tile and the rail
 * show what is waiting without the eight streams. Once the live stock has loaded for this tenant, the
 * live figures take over (they are the same sums, so nothing jumps).
 *
 * The same numbers are the capabilities' open work (D270): a count in flight or stock in transit
 * is what the pre-flight names before `counts` or `transfers` is switched off.
 */
export function inventoryAttentionProjections(): readonly AttentionProjection[] {
  const stock = inject(InventoryStockService);
  const summary = inject(InventorySummaryService);
  const cap = inject(CapabilityService);
  const store = inject(Store);

  const appModule = toSignal(store.select((s: any) => s?.app?.currentAppModuleId as string | null), { initialValue: null });
  const tenantId = toSignal(store.select((s: any) => s?.authState?.user?.tenantId as string | undefined), { initialValue: undefined });
  const stockTenant = toSignal(store.select((s: any) => s?.inventoryappstate?.stockTenantId as string | null | undefined), { initialValue: null });
  effect(() => {
    if (tenantId() && appModule() === 'inventory' && cap.moduleOn('inventory')) {
      untracked(() => stock.watch());
    }
  });
  /* The summary is one document: followed whenever Inventory is on, whatever app is open (kz2 M12 leftovers). */
  effect(() => {
    const t = tenantId();
    if (t && cap.moduleOn('inventory')) {
      untracked(() => summary.watch(t));
    } else {
      untracked(() => summary.stop());
    }
  });

  /** The live stock answers for THIS tenant: until then the summary does. */
  const live = computed(() => stock.loaded() && !!tenantId() && stockTenant() === tenantId());
  const out = computed(() => (live() ? needsAttentionAt(stock.items(), EVERYWHERE, 'out').length : summary.counts().out));
  const low = computed(() => (live() ? needsAttentionAt(stock.items(), EVERYWHERE, 'low').length : summary.counts().low));
  const openCounts = computed(() => (live() ? stock.counts().filter((c) => c.status !== 'posted').length : summary.counts().openCounts));
  const inTransit = computed(() => (live() ? stock.transfers().filter((t) => t.status === 'in-transit').length : summary.counts().inTransit));
  const awaiting = computed(() => (live() ? stock.orders().filter((o) => awaitingDelivery(o)).length : summary.counts().ordersAwaiting));
  /* No live stock and no summary DOCUMENT: the open work is unknown, not zero — a winding capability must not retire
     on it. "No document" is not "nothing open": a capability winds down only with work open, which would have written
     a summary; a missing one means not deployed or backfilled yet, refused, or an offline start's empty cache. */
  const unknown = computed(() => !live() && summary.doc() == null);

  cap.registerOpenWork({
    moduleId: 'inventory',
    capKey: 'counts',
    work: computed(() => {
      const n = openCounts();
      const label = 'counts not yet posted';
      const resolve = 'Post or finish them on Stock › Counts';
      return n ? { kind: 'docs' as const, n, label, resolve } : unknown() ? { kind: 'docs' as const, n: 0, label, resolve, pending: true } : null;
    }),
  });
  cap.registerOpenWork({
    moduleId: 'inventory',
    capKey: 'transfers',
    work: computed(() => {
      const n = inTransit();
      const label = 'transfers in transit';
      const resolve = 'Receive them on Stock › In transit';
      return n ? { kind: 'docs' as const, n, label, resolve } : unknown() ? { kind: 'docs' as const, n: 0, label, resolve, pending: true } : null;
    }),
  });

  return [
    {
      rule: { id: 'inventory.stock_attention', moduleId: 'inventory', capKey: null, railItemId: INVENTORY_RAIL.stock, enabled: true, scope: 'site' },
      count: computed(() => out() + low()),
      tone: computed<AttentionTone>(() => (out() > 0 ? 'danger' : 'warning')),
    },
    {
      rule: { id: 'inventory.counts_open', moduleId: 'inventory', capKey: 'counts', railItemId: INVENTORY_RAIL.stock, enabled: true, scope: 'site' },
      count: openCounts,
    },
    {
      rule: { id: 'inventory.in_transit', moduleId: 'inventory', capKey: 'transfers', railItemId: INVENTORY_RAIL.stock, enabled: true, scope: 'site' },
      count: inTransit,
      tone: computed<AttentionTone>(() => 'warning'),
    },
    {
      rule: { id: 'inventory.orders_awaiting', moduleId: 'inventory', capKey: 'purchase', railItemId: INVENTORY_RAIL.orders, enabled: true, scope: 'site' },
      count: awaiting,
      tone: computed<AttentionTone>(() => 'warning'),
    },
  ];
}

/** Register inventory's projections at the root (AppModule), so the shell can show them. */
export function provideInventoryAttention(): Provider[] {
  return [{ provide: ATTENTION_PROJECTIONS, useValue: inventoryAttentionProjections, multi: true }];
}
